import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";

import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";

const ExpensesCategoryNewEdit = () => {
  usePageTitle("overlayData?.categoryId" ? "Edit Category" : "New Category");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();

  const closeOverlay = () => setOverlayName("");

  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryIRSCode, setCategoryIRSCode] = useState("");
  const [subcategories, setSubcategories] = useState([]);
  const [, setEditingSubcategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [subcategoryErrors, setSubcategoryErrors] = useState({});

  useEffect(() => {
    if (overlayData) {
      setCategoryName(overlayData.categoryName || "");
      setCategoryDescription(overlayData.categoryDescription || "");
      setCategoryIRSCode(overlayData.categoryIRSCode || "");
      setSubcategories(overlayData.subcategories || []);
    }
  }, [overlayData]);

  const validateForm = () => {
    const newErrors = {};
    if (!categoryName.trim())
      newErrors.categoryName = "Category name is required";
    return newErrors;
  };

  const handleAddNewSubcategory = () => {
    const newSubcategory = {
      id: `temp-${Date.now()}`,
      Name: "",
      Description: "",
      isNew: true,
      isEditing: true,
    };
    setSubcategories([...subcategories, newSubcategory]);
    setEditingSubcategory(newSubcategory.id);
  };

  const handleSaveSubcategory = (subcategoryId) => {
    const subcategory = subcategories.find((sub) => sub.id === subcategoryId);

    if (!subcategory.Name.trim()) {
      setSubcategoryErrors((prev) => ({
        ...prev,
        [subcategoryId]: "Subcategory name is required",
      }));
      return;
    }

    // Check for duplicates (excluding current item)
    const isDuplicate = subcategories.some(
      (sub) =>
        sub.id !== subcategoryId &&
        sub.Name.toLowerCase() === subcategory.Name.trim().toLowerCase(),
    );

    if (isDuplicate) {
      setSubcategoryErrors((prev) => ({
        ...prev,
        [subcategoryId]: "A subcategory with this name already exists",
      }));
      return;
    }

    setSubcategories(
      subcategories.map((sub) =>
        sub.id === subcategoryId ? { ...sub, isEditing: false } : sub,
      ),
    );
    setSubcategoryErrors((prev) => {
      const { [subcategoryId]: _, ...rest } = prev;
      return rest;
    });
    setEditingSubcategory(null);
  };

  const handleEditSubcategory = (subcategoryId) => {
    setSubcategories(
      subcategories.map((sub) =>
        sub.id === subcategoryId ? { ...sub, isEditing: true } : sub,
      ),
    );
    setEditingSubcategory(subcategoryId);
  };

  const handleUpdateSubcategoryField = (subcategoryId, field, value) => {
    setSubcategories(
      subcategories.map((sub) =>
        sub.id === subcategoryId ? { ...sub, [field]: value } : sub,
      ),
    );
    if (field === "Name") {
      setSubcategoryErrors((prev) => {
        const { [subcategoryId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleRemoveSubcategory = (subcategoryId) => {
    setSubcategories(subcategories.filter((sub) => sub.id !== subcategoryId));
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const categoryData = {
        Name: categoryName,
        Description: categoryDescription,
        IRSCode: categoryIRSCode,
      };

      let categoryId = overlayData.categoryId;

      // Create or update category
      if (categoryId) {
        await api.put(`/expenses/categories/${categoryId}`, categoryData);
      } else {
        const response = await api.post("/expenses/categories", categoryData);
        categoryId = response.data.id;
      }

      // Handle subcategories
      const newSubcategories = subcategories.filter((sub) => sub.isNew);
      const deletedSubcategoryIds = (overlayData.subcategories || [])
        .filter(
          (oldSub) => !subcategories.find((newSub) => newSub.id === oldSub.id),
        )
        .map((sub) => sub.id);

      // Create new subcategories
      for (const sub of newSubcategories) {
        await api.post("/expenses/subcategories", {
          CategoryID: categoryId,
          Name: sub.Name,
          Description: sub.Description,
        });
      }

      // Delete removed subcategories
      for (const subId of deletedSubcategoryIds) {
        await api.delete(`/expenses/subcategories/${subId}`);
      }

      showSnackbar(
        overlayData.categoryId
          ? "Category updated successfully"
          : "Category created successfully",
        theme,
        "success",
      );

      if (overlayData.onSaved) {
        overlayData.onSaved();
      }
      closeOverlay();
    } catch (error) {
      console.error("Error saving category:", error);

      // Extract error message from response
      let errorMessage = "Error saving category";

      // Try to get error message from various sources
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        // Handle validation errors array
        errorMessage = error.response.data.errors
          .map((err) => err.message || err)
          .join(", ");
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Set error on the appropriate field
      if (errorMessage.toLowerCase().includes("name")) {
        setErrors((prev) => ({
          ...prev,
          categoryName: errorMessage,
        }));
      } else {
        // If it's not a name-specific error, show in snackbar
        showSnackbar(errorMessage, theme, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle = overlayData?.categoryId
    ? "Edit Expense Category"
    : "New Expense Category";

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.background.alt,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          maxHeight: "75%",
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
          }}
        >
          {/* Left side */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {pageTitle}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            width: "100%",
            borderRadius: "1.5rem",
            border: `1px solid ${theme.palette.background.default}`,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Category
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Category Name */}
            <Grid size={{ xs: 12, sm: 9 }}>
              <TextField
                label="Category Name"
                value={categoryName}
                onChange={(e) => {
                  setCategoryName(e.target.value);
                  setErrors((prev) => {
                    const { categoryName, ...rest } = prev;
                    return rest;
                  });
                }}
                error={!!errors.categoryName}
                helperText={errors.categoryName}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.MuiInputBase-root fieldset": {
                      borderColor: theme.palette.divider,
                    },
                  },
                }}
              />
            </Grid>

            {/* IRS Code */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="IRS Code (Optional)"
                value={categoryIRSCode}
                onChange={(e) => setCategoryIRSCode(e.target.value)}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.MuiInputBase-root fieldset": {
                      borderColor: theme.palette.divider,
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Description */}
          <TextField
            fullWidth
            label="Description (Optional)"
            value={categoryDescription}
            onChange={(e) => setCategoryDescription(e.target.value)}
            multiline
            rows={3}
            size="small"
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                "&.MuiInputBase-root fieldset": {
                  borderColor: theme.palette.divider,
                },
              },
            }}
          />

          <Divider sx={{ my: 0.75 }} />

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Subcategories ({subcategories.length})
            </Typography>
            <IconButton
              size="small"
              onClick={handleAddNewSubcategory}
              sx={{
                ml: 1,
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: "8px",
                width: "24px",
                height: "24px",
                color: theme.palette.primary.main,
              }}
            >
              <AddRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Subcategories List */}
          {subcategories.length > 0 && (
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                //mb: 2,
              }}
            >
              <Stack spacing={1.5}>
                {subcategories.map((sub) => (
                  <Box
                    key={sub.id}
                    sx={{
                      p: 1.5,
                      bgcolor: theme.palette.background.alt,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      display: "flex",
                      gap: 1,
                      alignItems: "flex-start",
                    }}
                  >
                    {sub.isEditing ? (
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <TextField
                          fullWidth
                          label="Subcategory Name"
                          value={sub.Name}
                          onChange={(e) =>
                            handleUpdateSubcategoryField(
                              sub.id,
                              "Name",
                              e.target.value,
                            )
                          }
                          error={!!subcategoryErrors[sub.id]}
                          helperText={subcategoryErrors[sub.id]}
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&.MuiInputBase-root fieldset": {
                                borderColor: theme.palette.divider,
                              },
                            },
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Description (Optional)"
                          value={sub.Description}
                          onChange={(e) =>
                            handleUpdateSubcategoryField(
                              sub.id,
                              "Description",
                              e.target.value,
                            )
                          }
                          multiline
                          rows={2}
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&.MuiInputBase-root fieldset": {
                                borderColor: theme.palette.divider,
                              },
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="500">
                          {sub.Name}
                        </Typography>
                        {sub.Description && (
                          <Typography variant="caption" color="text.secondary">
                            {sub.Description}
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {sub.isEditing ? (
                        <IconButton
                          size="small"
                          onClick={() => handleSaveSubcategory(sub.id)}
                          sx={{
                            color: theme.palette.success.main,
                          }}
                        >
                          <CheckRoundedIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleEditSubcategory(sub.id)}
                          sx={{
                            color: theme.palette.primary.main,
                          }}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveSubcategory(sub.id)}
                        sx={{
                          color: theme.palette.error.main,
                        }}
                      >
                        <DeleteRoundedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "100%",
              flexDirection: "row",
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={closeOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={closeOverlay}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Discard
              </Button>
            )}

            <Box p="0 .5rem" />

            <Tooltip
              title={isMobile && isSubmitting ? "Saving changes..." : ""}
              placement="top"
            >
              <span>
                {isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                ) : (
                  <Button
                    disabled={isSubmitting}
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<SaveRoundedIcon />}
                    onClick={handleSubmit}
                    sx={{
                      borderRadius: "12px",
                    }}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : overlayData?.categoryId
                        ? "Update"
                        : "Create"}
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ExpensesCategoryNewEdit;
